import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonToolbar,
  Col,
  Row,
} from 'react-bootstrap';
import { reduxForm, formPropTypes } from 'redux-form';
import moment from 'moment-timezone';


import {
  PTOTypeInputField,
  DateInputField,
  TicketInputField,
  EquipmentInputField,
  StartTimeInputField,
  EndTimeInputField,
  NotesInputField,
} from './TimeEntryFormComponents';

import '../../form.css';

const propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  isNewRecord: PropTypes.bool.isRequired,
  /* eslint-disable react/forbid-prop-types, react/require-default-props */
  formValues: PropTypes.object,
  APIErrorMessage: PropTypes.string,
  minEditDate: PropTypes.string.isRequired,
  maxEditDate: PropTypes.string.isRequired,
  timeZone: PropTypes.string.isRequired,
  dispatchHideTimeEntryEditorModal: PropTypes.func.isRequired,
  ...formPropTypes,
  /* eslint-enable react/forbid-prop-types, react/require-default-props */
};

export const FORM_NAME = 'editUserTimeEntry';

const validateDate = (values, props) => {
  const { date } = values;
  const { minEditDate, maxEditDate, timeZone } = props;

  // Validate user input date
  const dateMoment = moment.tz(date, 'MM/DD/YYYY', true, timeZone);
  const dateUnix = dateMoment.unix();
  if (!dateMoment.isValid()) {
    return 'Invalid Date';
  }

  // Validate edit window minimum and maximum dates
  const minEditDateMoment = moment.tz(minEditDate, timeZone);
  const maxEditDateMoment = moment.tz(maxEditDate, timeZone);
  if (!(minEditDateMoment.isValid() && maxEditDateMoment.isValid())) {
    return null;
  }

  // Validate edit window interval
  if (!(maxEditDateMoment.unix() >= minEditDateMoment.unix())) {
    return null;
  }

  // Enforce edit window
  if (dateUnix < minEditDateMoment.unix() || dateUnix > maxEditDateMoment.unix()) {
    const minDateFormatted = minEditDateMoment.format('MM/DD/YYYY');
    const maxDateFormatted = maxEditDateMoment.format('MM/DD/YYYY');
    return `Date must be between ${minDateFormatted} and ${maxDateFormatted}`;
  }

  return null;
};

const validate = (values, props) => {
  const errors = {};

  errors.date = validateDate(values, props);

  if (!values.start_time) {
    errors.start_time = 'Required';
  }

  if (!values.end_time) {
    errors.end_time = 'Required';
  }

  return errors;
};

const TimeEntryCreateEditFormWithDate = (props) => {
  const {
    handleSubmit, submitting, pristine, formValues,
    APIErrorMessage, isNewRecord, invalid, dispatchHideTimeEntryEditorModal,
  } = props;

  let hasTicketNumber = false;
  let hasEquipment = false;

  let ptoDisabled = false;
  let ticketNumberDisabled = false;
  let equipmentDisabled = false;

  if (formValues) {
    hasTicketNumber = !!formValues.ticket_number;
    hasEquipment = !!formValues.equipment;

    let isPtoSelected = false;
    if (formValues.pto_type !== 'none') {
      isPtoSelected = true;
    }

    ptoDisabled = (hasTicketNumber || hasEquipment);
    ticketNumberDisabled = isPtoSelected || hasEquipment;
    equipmentDisabled = isPtoSelected || hasTicketNumber;
  }


  return (
    <form onSubmit={handleSubmit}>
      {DateInputField()}
      <Row>
        <Col xs={12}>
          {PTOTypeInputField(ptoDisabled)}
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          {TicketInputField(ticketNumberDisabled)}
        </Col>
        <Col xs={6}>
          {EquipmentInputField(equipmentDisabled)}
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          {StartTimeInputField()}
        </Col>
        <Col xs={6}>
          {EndTimeInputField()}
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          {NotesInputField()}
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <ButtonToolbar className="pull-right">
            <Button
              bsStyle="warning"
              onClick={() => { dispatchHideTimeEntryEditorModal(); }}
            > Cancel
            </Button>
            <Button bsStyle="primary" type="submit" disabled={submitting || pristine || invalid}>
              {isNewRecord && 'Create'}
              {!isNewRecord && 'Update'}
            </Button>
          </ButtonToolbar>
          <label>{APIErrorMessage}</label>
        </Col>
      </Row>
    </form>
  );
};

TimeEntryCreateEditFormWithDate.propTypes = propTypes;

export default reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
  initialValues: {
    pto_type: 'none',
  },
  validate,
})(TimeEntryCreateEditFormWithDate);
